import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { AuthCard } from './AuthLayout';

export function Register() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError(t('auth.passwordMinLength'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          name,
          surname,
        }),
      });

      const rawText = await response.text();
      let parsed: { message?: string } | null = null;
      if (rawText) {
        try {
          parsed = JSON.parse(rawText) as { message?: string };
        } catch {
          parsed = null;
        }
      }

      if (!response.ok) {
        const errorMessage = parsed?.message;
        throw new Error(errorMessage || (rawText ? rawText : t('auth.registrationFailed')));
      }

      navigate('/login', { state: { registered: true } });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard title={t('auth.createAccountTitle')} subtitle={t('auth.createAccountSubtitle')}>
      {error && <div className="auth-error">{error}</div>}

      <form onSubmit={handleSubmit} className="auth-form">
        <div className="auth-row">
          <div className="auth-field">
            <label htmlFor="name" className="auth-label">
              {t('auth.name')}
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="given-name"
              className="auth-input"
              placeholder={t('auth.namePlaceholder')}
            />
          </div>

          <div className="auth-field">
            <label htmlFor="surname" className="auth-label">
              {t('auth.surname')}
            </label>
            <input
              id="surname"
              type="text"
              value={surname}
              onChange={(e) => setSurname(e.target.value)}
              autoComplete="family-name"
              className="auth-input"
              placeholder={t('auth.surnamePlaceholder')}
            />
          </div>
        </div>

        <div className="auth-field">
          <label htmlFor="email" className="auth-label">
            {t('auth.email')}
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            className="auth-input"
            placeholder={t('auth.emailPlaceholder')}
          />
        </div>

        <div className="auth-field">
          <label htmlFor="password" className="auth-label">
            {t('auth.password')}
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            autoComplete="new-password"
            className="auth-input"
            placeholder="••••••••"
          />
        </div>

        <div className="auth-field">
          <label htmlFor="confirmPassword" className="auth-label">
            {t('auth.confirmPassword')}
          </label>
          <input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={6}
            autoComplete="new-password"
            className="auth-input"
            placeholder="••••••••"
          />
        </div>

        <p className="auth-terms-note">
          {t('auth.termsAgreePrefix')}{' '}
          <Link to="/terms" className="auth-link">
            {t('auth.termsOfService')}
          </Link>{' '}
          {t('auth.and')}{' '}
          <Link to="/privacy" className="auth-link">
            {t('auth.privacyPolicy')}
          </Link>
          {t('auth.termsAgreeSuffix')}
        </p>

        <button type="submit" disabled={loading} className="auth-button">
          {loading ? t('auth.creatingAccount') : t('auth.createAccount')}
        </button>
      </form>

      <div className="auth-footer">
        {t('auth.hasAccount')}{' '}
        <Link to="/login" className="auth-link">
          {t('auth.signInLink')}
        </Link>
      </div>
    </AuthCard>
  );
}
